import { useEffect, useMemo, useRef, useState } from 'react'

const DEFAULT_CENTER = { lat: 36.1699, lng: -115.1398 }
const DEFAULT_ZOOM = 12

const loadGoogleMaps = () => {
  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google)
  if (window.__googleMapsPromise) return window.__googleMapsPromise

  const apiKey = document.querySelector('meta[name="google-maps-api-key"]')?.content
  if (!apiKey) {
    return Promise.reject(new Error('Missing Google Maps API key'))
  }

  window.__googleMapsPromise = new Promise((resolve, reject) => {
    const callbackName = '__initGoogleMaps'
    window[callbackName] = () => {
      if (window.google?.maps?.importLibrary) {
        resolve(window.google)
      } else {
        reject(new Error('Google Maps API failed to initialize'))
      }
      delete window[callbackName]
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&loading=async&libraries=marker,geometry&callback=${callbackName}`
    script.async = true
    script.defer = true
    script.onerror = reject
    document.head.appendChild(script)
  })

  return window.__googleMapsPromise
}

export default function GoogleMap({
  points = [],
  center,
  zoom = DEFAULT_ZOOM,
  className = '',
  enableDirections = false,
  destination,
  origin,
  useCurrentLocation = false,
  infoWindowEnabled = true,
  onMarkerClick,      // (point) => void — called on marker click; disables info window
  fitBounds = false,  // auto-fit to all markers on load
  fitAllTrigger,      // increment this value to re-fit to all markers imperatively
}) {
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const markersRef = useRef([])
  const directionsRendererRef = useRef(null)
  const directionsServiceRef = useRef(null)
  const userLocationRef = useRef(null)
  const [loadError, setLoadError] = useState(null)

  const mapCenter = useMemo(() => {
    if (center) return center
    if (points.length > 0) return { lat: points[0].lat, lng: points[0].lng }
    return DEFAULT_CENTER
  }, [center, points])

  useEffect(() => {
    let isMounted = true

    loadGoogleMaps()
      .then(async (google) => {
        if (!isMounted || !mapContainerRef.current) return
        if (!google?.maps?.importLibrary) {
          setLoadError('Google Maps failed to initialize')
          return
        }

        const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([
          google.maps.importLibrary('maps'),
          google.maps.importLibrary('marker'),
        ])

        if (!mapRef.current) {
          mapRef.current = new Map(mapContainerRef.current, {
            center: mapCenter,
            zoom,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            mapId: 'DEMO_MAP_ID',
          })
        } else {
          mapRef.current.setCenter(mapCenter)
          mapRef.current.setZoom(zoom)
        }

        markersRef.current.forEach((marker) => {
          if (marker?.setMap) {
            marker.setMap(null)
          } else if (marker && 'map' in marker) {
            marker.map = null
          }
        })
        // When onMarkerClick is provided, panel below map handles interaction —
        // disable the built-in info window so clicks don't open a popup too.
        const useInfoWindow = infoWindowEnabled && !onMarkerClick
        const infoWindow = useInfoWindow ? new google.maps.InfoWindow() : null

        const escapeHtml = (value) =>
          String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')

        markersRef.current = points.map((point) => {
          const position = { lat: point.lat, lng: point.lng }
          let marker = null
          let isAdvanced = false

          if (AdvancedMarkerElement) {
            marker = new AdvancedMarkerElement({
              position,
              map: mapRef.current,
              title: point.title || '',
            })
            isAdvanced = true
          } else {
            marker = new google.maps.Marker({
              position,
              map: mapRef.current,
              title: point.title || '',
            })
          }

          const clickEvent = isAdvanced ? 'gmp-click' : 'click'

          if (onMarkerClick && marker?.addListener) {
            marker.addListener(clickEvent, () => onMarkerClick(point))
          } else if (infoWindow && (point.infoHtml || point.title)) {
            const html = point.infoHtml
              ? point.infoHtml
              : `<div><strong>${escapeHtml(point.title || '')}</strong></div>`
            if (marker?.addListener) {
              marker.addListener(clickEvent, () => {
                infoWindow.setContent(html)
                infoWindow.open({ anchor: marker, map: mapRef.current })
              })
            }
          }

          return marker
        })

        // Auto-fit to all markers
        if ((fitBounds || fitAllTrigger !== undefined) && points.length > 1) {
          const bounds = new google.maps.LatLngBounds()
          points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
          mapRef.current.fitBounds(bounds)
        }

        if (enableDirections && destination) {
          if (!directionsServiceRef.current) {
            directionsServiceRef.current = new google.maps.DirectionsService()
          }
          if (!directionsRendererRef.current) {
            directionsRendererRef.current = new google.maps.DirectionsRenderer({
              suppressMarkers: false,
            })
            directionsRendererRef.current.setMap(mapRef.current)
          }

          const routeBetween = (start) => {
            directionsServiceRef.current.route(
              {
                origin: start,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === 'OK' && result) {
                  directionsRendererRef.current.setDirections(result)
                }
              }
            )
          }

          if (origin) {
            routeBetween(origin)
          } else if (useCurrentLocation && navigator.geolocation) {
            // Use cached location if available, otherwise request once
            if (userLocationRef.current) {
              routeBetween(userLocationRef.current)
            } else {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  const userPos = { lat: pos.coords.latitude, lng: pos.coords.longitude }
                  userLocationRef.current = userPos
                  routeBetween(userPos)
                },
                () => {
                  routeBetween(mapCenter)
                }
              )
            }
          } else {
            routeBetween(mapCenter)
          }
        } else if (directionsRendererRef.current) {
          directionsRendererRef.current.setMap(null)
          directionsRendererRef.current = null
        }
      })
      .catch((error) => {
        if (isMounted) setLoadError(error.message || 'Failed to load Google Maps')
      })

    return () => {
      isMounted = false
    }
  }, [points, mapCenter, zoom, enableDirections, destination, origin, useCurrentLocation, infoWindowEnabled, onMarkerClick, fitBounds, fitAllTrigger])

  if (loadError) {
    return (
      <div className={`rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 ${className}`}>
        {loadError}
      </div>
    )
  }

  return <div ref={mapContainerRef} className={className} />
}
